import * as React from "react";

/**
 * Global custom event name that the context-menu overlay listens to.
 */
export const OPEN_CONTEXT_MENU_EVENT = "context-menu:open" as const;

/**
 * Context-menu item definitions.
 */
export interface SeparatorItem {
    type: "separator";
    /**
     * Optional className for the separator.
     */
    className?: string;
}

export interface HeaderItem {
    type: "header";
    /**
     * Header label shown in muted style.
     */
    label: string;
    /**
     * Optional className for the header.
     */
    className?: string;
}

export interface ActionItem {
    type: "item";
    label: string;
    onClick: () => void;
    /**
     * React node rendered on the left side of the label (e.g. lucide-react icon).
     */
    icon?: React.ReactNode;
    /**
     * Optional className for the item.
     */
    className?: string;
    /**
     * Optional tooltip text.
     */
    tooltip?: string;
    disabled?: boolean;
}

export interface DropdownItem {
    type: "dropdown";
    label: string;
    children: ContextMenuItem[];
    icon?: React.ReactNode;
    tooltip?: string;
    /**
     * Optional className for the item.
     */
    className?: string;
}

export type ContextMenuItem =
    | SeparatorItem
    | HeaderItem
    | ActionItem
    | DropdownItem;

/**
 * Programmatically open a context-menu at a given screen position.
 * Internal – prefer using `triggerContextMenu` / `useContextMenu` in components.
 */
export function openContextMenu(
    position: { x: number; y: number },
    items: ContextMenuItem[],
) {
    window.dispatchEvent(
        new CustomEvent(OPEN_CONTEXT_MENU_EVENT, {
            detail: { position, items },
        }),
    );
}

/**
 * Trigger a context-menu in an `onContextMenu` handler.
 * Prevents the browser default menu and emits our global event.
 */
export function triggerContextMenu<T = HTMLElement>(
    event: React.MouseEvent<T, MouseEvent>,
    items: ContextMenuItem[],
) {
    event.preventDefault();
    event.stopPropagation();

    // Use clientX/Y to respect viewport (scroll not needed because menu is fixed).
    openContextMenu({ x: event.clientX, y: event.clientY }, items);
}

/**
 * Convenience hook that returns an `onContextMenu` handler for the given items.
 *
 * Example:
 * const handleContextMenu = useContextMenu(myItems);
 * <div onContextMenu={handleContextMenu}>…</div>
 */
export function useContextMenu(items: ContextMenuItem[] | ((e: React.MouseEvent) => ContextMenuItem[])) {
    return React.useCallback(
        (e: React.MouseEvent) => {
            if (typeof items === 'function') {
                return triggerContextMenu(e, items(e));
            }
            return items?.length ? triggerContextMenu(e, items) : undefined;
        },
        [items],
    );
} 